import React, { useState, useEffect } from "react";

import { QueryClient, QueryClientProvider } from "react-query";
import lightBackground from '../src/assets/wa-background-light.png';
import darkBackground from '../src/assets/wa-background-dark.jpg';
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';

import Routes from "./routes";

const queryClient = new QueryClient();

const App = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const storedPref = window.localStorage.getItem("preferredTheme");
    const [themePreference, setThemePreference] = useState(storedPref || "system");

    // Resolve actual mode from preference
    const mode = themePreference === "system"
        ? (prefersDarkMode ? "dark" : "light")
        : themePreference;

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setThemePreference((prev) => {
                    const resolved = prev === "system" ? (prefersDarkMode ? "dark" : "light") : prev;
                    return resolved === "light" ? "dark" : "light";
                });
            },
            setThemeMode: (newMode) => {
                setThemePreference(newMode);
            },
            themePreference,
        }),
        [themePreference, prefersDarkMode]
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
                    borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.15)',
                    backgroundColor: mode === "light" ? "#c4c4c4" : "#374045",
                    borderRadius: "8px",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                    borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#d1d7db" : "#374045",
                    borderRadius: "8px",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)" },
                secondary: { main: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)" },
                background: {
                    default: mode === "light" ? "#f0f2f5" : "#0b141a",
                    paper: mode === "light" ? "#ffffff" : "#111b21",
                },
                text: {
                    primary: mode === "light" ? "#111b21" : "#e9edef",
                    secondary: mode === "light" ? "#667781" : "#8696a0",
                },
                divider: mode === "light" ? "#e9edef" : "#222d34",
                // Custom palette tokens
                sair: { main: mode === "light" ? "#ea0038" : "#ea0038" },
                vcard: { main: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)" },
                textPrimary: mode === "light" ? "#111b21" : "#e9edef",
                borderPrimary: mode === "light" ? "#e9edef" : "#222d34",
                dark: { main: mode === "light" ? "#111b21" : "#e9edef" },
                light: { main: mode === "light" ? "#f0f2f5" : "#0b141a" },
                tabHeaderBackground: mode === "light" ? "#f0f2f5" : "#202c33",
                optionsBackground: mode === "light" ? "#f0f2f5" : "#111b21",
                options: mode === "light" ? "#f0f2f5" : "#202c33",
                fontecor: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)",
                fancyBackground: mode === "light" ? "#f0f2f5" : "#0b141a",
                bordabox: mode === "light" ? "#e9edef" : "#222d34",
                newmessagebox: mode === "light" ? "#f0f2f5" : "#202c33",
                inputdigita: mode === "light" ? "#ffffff" : "#2a3942",
                contactdrawer: mode === "light" ? "#ffffff" : "#111b21",
                announcements: mode === "light" ? "#f0f2f5" : "#111b21",
                login: mode === "light" ? "#ffffff" : "#111b21",
                announcementspopover: mode === "light" ? "#ffffff" : "#202c33",
                chatlist: mode === "light" ? "#f0f2f5" : "#111b21",
                boxlist: mode === "light" ? "#f0f2f5" : "#111b21",
                boxchatlist: mode === "light" ? "#ffffff" : "#111b21",
                total: mode === "light" ? "#ffffff" : "#111b21",
                messageIcons: mode === "light" ? "#667781" : "#8696a0",
                inputBackground: mode === "light" ? "#ffffff" : "#2a3942",
                barraSuperior: mode === "light" ? "rgba(255,53,0,0.8)" : "#202c33",
                boxticket: mode === "light" ? "#f0f2f5" : "#0b141a",
                campaigntab: mode === "light" ? "#f0f2f5" : "#202c33",
                mediainput: mode === "light" ? "#f0f2f5" : "#2a3942",
                // WhatsApp-specific tokens
                sidebarBackground: mode === "light" ? "#ffffff" : "#111b21",
                sidebarHover: mode === "light" ? "#f0f2f5" : "#202c33",
                sidebarActive: mode === "light" ? "#f0f2f5" : "#2a3942",
                sidebarIcon: mode === "light" ? "#54656f" : "#aebac1",
                sidebarIconActive: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)",
                chatHeader: mode === "light" ? "#f0f2f5" : "#202c33",
                messageOutgoing: mode === "light" ? "#d9fdd3" : "#005c4b",
                messageIncoming: mode === "light" ? "#ffffff" : "#202c33",
                accent: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)",
            },
            mode,
            overrides: {
                MuiPaper: {
                    rounded: { borderRadius: 10 },
                },
                MuiCard: {
                    root: { borderRadius: 10 },
                },
                MuiDialog: {
                    paper: { borderRadius: 10 },
                },
                MuiButton: {
                    root: { borderRadius: 8, textTransform: "none" },
                    containedPrimary: {
                        backgroundColor: mode === "light" ? "rgba(255,53,0,0.8)" : "rgba(255,53,0,0.8)",
                        color: "#ffffff",
                        "&:hover": {
                            backgroundColor: mode === "light" ? "#cc2a00" : "#cc2a00",
                        },
                    },
                },
                MuiOutlinedInput: {
                    root: { borderRadius: 10 },
                },
                MuiTab: {
                    root: { textTransform: "none" },
                },
                MuiTooltip: {
                    tooltip: {
                        borderRadius: 6,
                        fontSize: 12,
                    },
                },
                MuiMenu: {
                    paper: {
                        borderRadius: 10,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    },
                },
                MuiPopover: {
                    paper: {
                        borderRadius: 10,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    },
                },
                MuiListItem: {
                    root: {
                        borderRadius: 8,
                    },
                },
            },
        },
        locale
    );

    // Define CSS variables for global components (used by calendar and pages)
    React.useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--bg', theme.palette.background.default);
        root.style.setProperty('--paper', theme.palette.background.paper);
        root.style.setProperty('--text-primary', theme.palette.text.primary);
        root.style.setProperty('--text-secondary', theme.palette.text.secondary);
        root.style.setProperty('--border', theme.palette.borderPrimary);
        root.style.setProperty('--primary', theme.palette.primary.main);
        root.style.setProperty('--calendar-cell-bg', mode === 'light' ? '#ffffff' : '#0b141a');
        root.style.setProperty('--calendar-text', mode === 'light' ? '#111b21' : '#e9edef');
        root.style.setProperty('--calendar-today-bg', mode === 'light' ? '#f5f7fa' : '#122026');
        root.style.setProperty('--calendar-event-bg', mode === 'light' ? '#ffdede' : '#cc2a00');
        root.style.setProperty('--muted', mode === 'light' ? '#667781' : '#8696a0');
    }, [theme, mode]);

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

        if (browserLocale === "ptBR") {
            setLocale(ptBR);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", themePreference);
    }, [themePreference]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
